import { Injector } from '@angular/core';
import { getSha256HashAsync } from '@common/index';
import { environment } from '@environment';
import { IBlockchain } from '@services/api/blockchain';
import { sleep } from '@services/utils';
import { ethers, FixedNumber } from 'ethers';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';

import { EVMProviderFactoryService } from '@services/provider/evm-provider-factory.service';
import { INftByBlockchain, IWalletNft, NftObserver, TokenUrlType } from './base-nft-observer.service';
import ERC1155_METADATA_ABI from './IERC1155Metadata.json';
import ERC721_METADATA_ABI from './IERC721Metadata.json';

export const NFT_SPECIFICATION = {
    erc721: 'erc-721',
    erc1155: 'erc-1155',
} as const;

export type T_NFT_SPECIFICATION = (typeof NFT_SPECIFICATION)[ keyof typeof NFT_SPECIFICATION ];

interface ILog
{
    blockNumber: string;
    timeStamp: string;
    hash: string;
    nonce: string;
    blockHash: string;
    transactionIndex: string;
    gas: string;
    gasPrice: string;
    gasUsed: string;
    cumulativeGasUsed: string;
    input: string;
    contractAddress: string;
    from: string;
    to: string;
    tokenID: string;
    tokenValue: string;
    tokenName: string;
    tokenSymbol: string;
    confirmations: string;
}

export interface ILogNFT extends ILog
{
    type: T_NFT_SPECIFICATION;
}


export class NftObserverService extends NftObserver
{
    protected nftWalletByBlockchain: INftByBlockchain[] = [];
    protected collapseNftWallet: boolean = true;
    protected isFirstLoadNFT: boolean = false;
    protected totalLogs: number = 0;
    protected totalNFT: { count: number; } = { count: 0 };
    protected isProcessing: ReplaySubject<boolean> = new ReplaySubject(1);


    constructor(
        protected injector: Injector,
        _walletAddress: string
    )
    {
        super(injector, _walletAddress);
        // this.init()
    }

    protected async init()
    {
        const blockchains: IBlockchain[] = await this.networksService.getBlockchains(null, false, true);
        const { blockNFT, amountLogs, totalNFT } = await this.enableBlockchain(blockchains);

        this.totalNFT.count = totalNFT;
        this.nftWalletByBlockchain = blockNFT;
        this.totalLogs = amountLogs;

    }

    public async getNewNFTData()
    {
        if (!this.isFirstLoadNFT)
        {
            await this.reloadNFTStore();
            this.isFirstLoadNFT = true;
        }
        else
        {
            this.getNFTStore();
        }

        return { nftAccount: this.nftWalletByBlockchain, totalNFT: this.totalNFT, collapseNftWallet: this.collapseNftWallet };
    }

    public async findNFTInStore(_chainId: number, _contractAddress: string, _tokenId: string)
    {
        await this.getNewNFTData();

        if (this.nftWalletByBlockchain)
        {
            for (let block of this.nftWalletByBlockchain)
            {
                if (block.blockchain.chain_id == _chainId && block.walletNFT[ _contractAddress ])
                {
                    return block.walletNFT[ _contractAddress ];
                }
            }
        }

        return null;
    }

    public async getNFTStore()
    {
        await this.isProcessing.pipe(take(1)).toPromise();

        this.updateNFTLogs();

        return { nftAccount: this.nftWalletByBlockchain, totalNFT: this.totalNFT, collapseNftWallet: this.collapseNftWallet };
    }

    public handleCollapse()
    {
        return this.collapseNftWallet = !this.collapseNftWallet;
    }

    public async reloadNFTStore()
    {
        this.clearBuffer();
        await this.init();
        this.isProcessing.next(false);
    }

    public reloadNFTStoreAgain()
    {
        this.isFirstLoadNFT = false;
    }

    private clearBuffer()
    {
        this.isProcessing = new ReplaySubject(1);
    }

    public async reloadNFTStoreByChainId(_chainId: number)
    {
        this.clearBuffer();
        if (!this.nftWalletByBlockchain.find(v => v.blockchain.chain_id == _chainId))
        {
            const blockchains: IBlockchain[] = (await this.networksService.getBlockchains(null, false, true)).filter((v) => v.chain_id == _chainId);
            const { blockNFT, amountLogs, totalNFT } = await this.enableBlockchain(blockchains, false);

            this.totalNFT.count += totalNFT;

            this.nftWalletByBlockchain.push(...blockNFT);
        }
        this.isProcessing.next(false);
    }

    protected async enableBlockchain(_blockchains: IBlockchain[], _isUseCache: boolean = true)
    {
        // const blockchains: IBlockchain[] = await this.networksService.getBlockchains(null, false, true);
        const blockNFT = [];
        let amountLogs: number = 0;
        let totalNFT: number = 0;

        for (let block of _blockchains)
        {
            let cacheNftBlock = null;
            if (_isUseCache)
            {
                cacheNftBlock = await this.GetFromIndexedDb(block.chain_id);

                // blockNumber
                console.log("🚀 ~ file: nft-observer.service.ts:135 ~ cacheNftBlock:", cacheNftBlock);
            }

            let findNFT: IWalletNft, countLogs, blockNumber, lastLogs;
            if (cacheNftBlock === null)
            {
                const NFTByBlock = await this.loadNFTHistory(block.chain_id);
                console.log("🚀 ~ file: nft-observer.service.ts:142 ~ NFTByBlock:", NFTByBlock);
                findNFT = NFTByBlock.accountNft;
                countLogs = NFTByBlock.countLogs;
                blockNumber = NFTByBlock.blockNumber;
                lastLogs = NFTByBlock.lastLogs;
            }
            else
            {
                const NFTByBlock = await this.updateNFTData(block.chain_id,
                    { erc721Block: cacheNftBlock.nftByBlockchain.blockNumber ?? 0, erc1155Block: cacheNftBlock.nftByBlockchain.blockNumberErc1155 ?? 0 },
                    cacheNftBlock.nftByBlockchain.walletNFT,
                    { erc721Logs: cacheNftBlock.nftByBlockchain.lastLogs ?? [], erc1155Logs: cacheNftBlock.nftByBlockchain.lastLogsErc1155 ?? [] }
                );
                findNFT = NFTByBlock.accountNft;
                countLogs = NFTByBlock.countLogs;
                blockNumber = NFTByBlock.blockNumber;
                lastLogs = NFTByBlock.lastLogs;
            }

            let amountNFT: number = 0;

            for (let countNft in findNFT)
            {
                amountNFT += findNFT[ countNft ].amount;
            }

            if (amountNFT > 0)
            {
                const nftByBlockchain = {
                    blockchain: block,
                    walletNFT: findNFT,
                    amountNFT: amountNFT,
                    collapse: true,
                    blockNumber: blockNumber.erc721Block,
                    lastLogs: lastLogs.erc721Logs,
                    blockNumberErc1155: blockNumber.erc1155Block,
                    lastLogsErc1155: lastLogs.erc1155Logs,
                };
                blockNFT.push(nftByBlockchain);
                totalNFT += amountNFT;

                if (cacheNftBlock === null || cacheNftBlock.amountNFT < amountNFT)
                {
                    await this.setToIndexedDb(nftByBlockchain, block.chain_id, amountNFT);
                }

            }
            amountLogs += countLogs;

        }

        return { blockNFT, amountLogs, totalNFT };
    }

    private async delToIndexedDb(_chainId)
    {
        const strKey = _chainId.toString() + this.walletAddress.toLowerCase();
        const key = 'nftData' + (await getSha256HashAsync(strKey));
        return await this.indexDBService.del(key);
    }

    private async setToIndexedDb(_nftBlock, _chainId, _amountNFT)
    {
        const strKey = _chainId.toString() + this.walletAddress.toLowerCase();
        const key = 'nftData' + (await getSha256HashAsync(strKey));
        await this.indexDBService.set(key, { nftByBlockchain: _nftBlock, amountNFT: _amountNFT });
    }

    private async GetFromIndexedDb(_chainId)
    {
        const strKey = _chainId.toString() + this.walletAddress.toLowerCase();
        const key = 'nftData' + (await getSha256HashAsync(strKey));
        return await this.indexDBService.get(key);
    }

    public async updateNFTLogs()
    {
        const blockchainsId: number[] = [];

        let countNFT = 0;
        for (let nftByBlock of this.nftWalletByBlockchain)
        {
            blockchainsId.push(nftByBlock.blockchain.id);
            const beforeAmount = nftByBlock.amountNFT;
            const NFTByBlock = await this.updateNFTData(nftByBlock.blockchain.chain_id,
                { erc721Block: nftByBlock.blockNumber ?? 0, erc1155Block: nftByBlock.blockNumberErc1155 ?? 0 },
                nftByBlock.walletNFT,
                { erc721Logs: nftByBlock.lastLogs ?? [], erc1155Logs: nftByBlock.lastLogsErc1155 ?? [] }
            );

            if (nftByBlock.lastLogs === NFTByBlock.lastLogs.erc721Logs && nftByBlock.lastLogsErc1155 === NFTByBlock.lastLogs.erc1155Logs)
            {
                countNFT += beforeAmount;

                continue;
            }
            let amountNFT: number = 0;

            for (let countNft in NFTByBlock.accountNft)
            {
                amountNFT += NFTByBlock.accountNft[ countNft ].amount;
            }

            nftByBlock.amountNFT = amountNFT;
            nftByBlock.walletNFT = NFTByBlock.accountNft;
            nftByBlock.lastLogs = NFTByBlock.lastLogs.erc721Logs;
            nftByBlock.blockNumber = NFTByBlock.blockNumber.erc721Block;
            nftByBlock.lastLogsErc1155 = NFTByBlock.lastLogs.erc1155Logs;
            nftByBlock.blockNumberErc1155 = NFTByBlock.blockNumber.erc1155Block;

            if (amountNFT > 0)
            {
                countNFT += amountNFT;
                await this.setToIndexedDb(nftByBlock, nftByBlock.blockchain.chain_id, amountNFT);
            }
            else if (beforeAmount > 0)
            {
                await this.delToIndexedDb(nftByBlock.blockchain.chain_id);
            }
        }
        this.totalNFT.count = countNFT;

        return blockchainsId;
    }

    // public async getNFTHistory()
    // {
    //     const { blockNFT, amountLogs } = await this.enableBlockchain();

    //     if (this.totalLogs !== amountLogs)
    //     {
    //         this.nftWalletByBlockchain = blockNFT;
    //     }
    //     return { nftAccount: this.nftWalletByBlockchain, totalNFT: this.totalNFT };
    // }

    protected async updateNFTData(_chainId: number, _blockNumber: { erc721Block: any, erc1155Block: any }, _cacheNft: IWalletNft, _lastLogs: { erc721Logs: { blockNumber: string, hash: string; }[], erc1155Logs: { blockNumber: string, hash: string; }[] })
    {
        const chainId = _chainId;
        const address = this.walletAddress.toLowerCase();

        const url = await this.prepareUrl(chainId, address);
        let logs = await this.loadLogs(url, _blockNumber.erc721Block);

        const uniqueLogs = [], uniqueLogsErc1155 = [];
        // const lastLog = _lastLogs.at(-1);
        // const lastLogIndex = logs.findIndex(log => log.hash === lastLog.hash && log.blockNumber === lastLog.blockNumber);
        // const lastLogsTrim = [ ...logs ].slice(lastLogIndex + _lastLogs.length);
        logs.map(v =>
        {
            if (_lastLogs.erc721Logs.findIndex(val => val.hash === v.hash) === -1)
            {
                uniqueLogs.push(v);
            }
        });

        const erc1155Logs = await this.loadERC1155Logs(chainId, address, _blockNumber.erc1155Block);
        // uniqueLogs.push(...erc1155Logs);

        erc1155Logs.map(v =>
        {
            if (_lastLogs.erc1155Logs.findIndex(val => val.hash === v.hash) === -1)
            {
                uniqueLogsErc1155.push(v);
            }
        });

        // const accountNft = this.findAccountNFT(lastLogsTrim, address, _cacheNft);
        const accountNft = this.findAccountNFT(uniqueLogs, address, _cacheNft);
        const accountNftAll = this.findAccountNFT(uniqueLogsErc1155, address, accountNft);

        this.filterRestNFT(accountNft);

        let blockNumber: { erc721Block: any, erc1155Block: any } = { erc721Block: 0, erc1155Block: 0 };
        let lastLogs: { erc721Logs: { blockNumber: string, hash: string; }[], erc1155Logs: { blockNumber: string, hash: string; }[] } = { erc721Logs: [], erc1155Logs: [] };
        if (uniqueLogs.length > 0)
        {
            blockNumber.erc721Block = logs.at(-1).blockNumber;
            lastLogs.erc721Logs = logs.filter(v => v.blockNumber == blockNumber.erc721Block).map(v => { return { blockNumber: v.blockNumber, hash: v.hash }; });
        }
        else
        {
            blockNumber.erc721Block = _blockNumber.erc721Block;
            lastLogs.erc721Logs = _lastLogs.erc721Logs;
        }

        if (uniqueLogsErc1155.length > 0)
        {
            blockNumber.erc1155Block = uniqueLogsErc1155.at(-1).blockNumber;
            lastLogs.erc1155Logs = uniqueLogsErc1155.filter(v => v.blockNumber == blockNumber.erc1155Block).map(v => { return { blockNumber: v.blockNumber, hash: v.hash }; });
        }
        else
        {
            blockNumber.erc1155Block = _blockNumber.erc1155Block;
            lastLogs.erc1155Logs = _lastLogs.erc1155Logs;
        }

        return { accountNft: accountNftAll, countLogs: logs.length, blockNumber, lastLogs };
    }

    protected async loadNFTHistory(_chainId: number)
    {
        const chainId = _chainId;
        const address = this.walletAddress.toLowerCase();

        const url = await this.prepareUrl(chainId, address);
        const logs = await this.loadLogs(url, 0);


        const erc1155Logs = await this.loadERC1155Logs(chainId, address);


        const accountNft = this.findAccountNFT(logs, address);

        const accountNftAll = this.findAccountNFT(erc1155Logs, address, accountNft);

        // let blockNumber;
        // let lastLogs;
        let blockNumber: { erc721Block: any, erc1155Block: any } = { erc721Block: 0, erc1155Block: 0 };
        let lastLogs: { erc721Logs: { blockNumber: string, hash: string; }[], erc1155Logs: { blockNumber: string, hash: string; }[] } = { erc721Logs: [], erc1155Logs: [] };

        if (logs.length > 0)
        {
            // blockNumber = logs[ logs.length - 1 ].blockNumber;
            // lastLogs = logs.filter(v => v.blockNumber === blockNumber).map(v => { return { blockNumber: v.blockNumber, hash: v.hash }; });
            blockNumber.erc721Block = logs.at(-1).blockNumber;
            lastLogs.erc721Logs = logs.filter(v => v.blockNumber === blockNumber.erc721Block).map(v => { return { blockNumber: v.blockNumber, hash: v.hash }; });
        }
        if (erc1155Logs.length > 0)
        {
            blockNumber.erc1155Block = erc1155Logs.at(-1).blockNumber;
            lastLogs.erc1155Logs = erc1155Logs.filter(v => v.blockNumber == blockNumber.erc1155Block).map(v => { return { blockNumber: v.blockNumber, hash: v.hash }; });
        }

        return { accountNft: accountNftAll, countLogs: logs.length, blockNumber, lastLogs };
    }

    private async filterRestNFT(accountNft: IWalletNft)
    {

        for (let contract in accountNft)
        {
            if (accountNft[ contract ]?.nftData?.length > 0 && accountNft[ contract ]?.nftData?.length > accountNft[ contract ].amount)
            {
                accountNft[ contract ].nftData = accountNft[ contract ].nftData.filter(v => accountNft[ contract ].tokenId.find(tokenId => tokenId == v.tokenId));
            }
        }
    }

    public async loadNFTByChainId(_chainId: number)
    {
        const blockchain = await this.networksService.getBlockchainByChainId(_chainId);

        const findNFT = await this.loadNFTByBlockchain(this.walletAddress, blockchain.chain_id);

        let amountNFT: number = 0;

        for (let countNft in findNFT)
        {
            amountNFT += findNFT[ countNft ].amount;
        }

        if (amountNFT > 0)
        {
            return { blockchain: blockchain, walletNFT: findNFT, amountNFT: amountNFT, collapse: true };
        }
        return null;
    }

    public async loadAccountNFTTokens(_walletAddress: string)
    {
        const blockchains = await this.networksService.getBlockchains(null, false, true);

        const loadNFT = [];
        for (let block of blockchains)
        {
            const findNFT = await this.loadNFTByBlockchain(_walletAddress, block.chain_id);

            let amountNFT: number = 0;

            for (let countNft in findNFT)
            {
                amountNFT += findNFT[ countNft ].amount;
            }

            if (amountNFT > 0)
            {
                loadNFT.push({ blockchain: block, walletNFT: findNFT, amountNFT: amountNFT, collapse: true });
            }

        }

        return loadNFT;
    }

    protected async prepareUrl(_chainId: number, _address: string): Promise<string>
    {
        const blockchain = environment[ _chainId ];
        const apiKey = blockchain.ExplorerApi;
        const host = (await this.networksService.getBlockchains()).find(v => v.chain_id === _chainId).explorer;

        const url = `${ host }?module=account&action=tokennfttx&address=${ _address }&sort=asc&apikey=${ apiKey }`;

        return url;
    }

    protected async prepareUrlERC1155(chainId: number, address: string): Promise<string>
    {
        const blockchain = environment[ chainId ];
        const apiKey = blockchain.ExplorerApi;
        const host = (await this.networksService.getBlockchains()).find(v => v.chain_id === chainId).explorer;

        return `${ host }?module=account&action=token1155tx&address=${ address }&sort=asc&apikey=${ apiKey }`;
    }

    public async loadNFTByBlockchain(_walletAddress: string, _chainId: number)
    {
        const chainId = _chainId;
        const address = _walletAddress.toLowerCase();
        // const address = '0xE7b30A037F5598E4e73702ca66A59Af5CC650Dcd';
        // const address2 = '0x6975be450864c02b4613023c2152ee0743572325';

        // 'https://api.etherscan.io/api?module=account&action=tokennfttx&address=0x6975be450864c02b4613023c2152ee0743572325&startblock=0&sort=asc&apikey=YourApiKeyToken'
        const url = await this.prepareUrl(chainId, address);
        const logs = await this.loadLogs(url, 0);

        const accountNft = this.findAccountNFT(logs, address);

        const prepareNFTToken = await this.getDataNFTToken(accountNft, chainId);

        return accountNft;
    }

    public async getDataNFTByTokenId(_contractAddress: string, _chainId: number, _tokenId: string, _explorer: string = null)
    {
        let nftData;
        const { contract, name, symbol } =
            await this.contractFactory.create(_contractAddress, _chainId, ERC721_METADATA_ABI, null, _explorer);
        try
        {
            const owner = await contract.ownerOf(_tokenId);

            const dataNFT = await this.loadTokenUrl(_tokenId, contract);

            nftData = {
                ...dataNFT,
                contractAddress: _contractAddress,
                contractName: name,
                owner: owner,
                tokenId: _tokenId,
                tokenType: 'ERC-721',
                hash: null,
            };
        }
        catch (e)
        {
            return this.getErc1155Data(_contractAddress, _chainId, _tokenId, _explorer);
        }

        return nftData;
    }

    private async getErc1155Data(_contractAddress: string, _chainId: number, _tokenId: string, _explorer: string = null)
    {
        let metaData = {
            name: 'ERC115511',
            symbol: 'ERC115511'
        };
        let nftData;
        const { contract } = await this.contractFactory.create(
            _contractAddress,
            _chainId,
            ERC1155_METADATA_ABI,
            metaData,
            _explorer,
            true
        );
        const URI = await contract.uri(_tokenId);

        try
        {
            const url = URI.replace('{id}', _tokenId);
            // const contractMeta = await this.http.post<any>('/api/nft-endpoint/get-1155-meta', {
            //     url
            // }).toPromise();
            // metaData = {
            //     name: contractMeta.data.name,
            //     symbol: contractMeta.data.name
            // };

            const dataNFT = await this.loadTokenUrl(_tokenId, contract);

            nftData = {
                ...dataNFT,
                contractAddress: _contractAddress,
                contractName: metaData.name,
                symbol: metaData.symbol,
                // owner: owner,
                tokenId: _tokenId,
                tokenType: 'ERC-1155',
                hash: null,
            };

        }
        catch (e)
        {
            console.error(e);
        }

        return nftData;
    }

    public async getDataNFTToken(accountNft, _chainId: number)
    {
        let resNFTToken = [];
        for (let contractAddress in accountNft)
        {
            if (accountNft[ contractAddress ].amount < 1)
            {
                continue;
            }


            let contract;

            if (accountNft[ contractAddress ].isErc1155)
            {
                const provider = this.injector.get(EVMProviderFactoryService).getProvider(_chainId);
                contract = new ethers.Contract(contractAddress, ERC1155_METADATA_ABI, provider);
            }
            else
            {
                const { contract: dataContract, name, symbol } =
                    await this.contractFactory.create(
                        contractAddress,
                        _chainId,
                        ERC721_METADATA_ABI,
                        { symbol: accountNft[ contractAddress ].symbol, name: accountNft[ contractAddress ].name }
                    );
                contract = dataContract;
            }

            console.log(accountNft[ contractAddress ], contract);

            accountNft[ contractAddress ].nftData = [];
            // resNFTToken[ contractAddress ] = [];

            for (let tokenId of accountNft[ contractAddress ].tokenId)
            {
                try
                {
                    const dataNFT = await this.getMetadataAsync(tokenId, contract);

                    accountNft[ contractAddress ].nftData.push({
                        ...dataNFT,
                        contractAddress,
                        tokenId: tokenId,
                        tokenType: accountNft[ contractAddress ].isErc1155 ? 'ERC-1155' : 'ERC-721',
                        hash: accountNft[ contractAddress ].tokenHash[ tokenId.toString() ],
                        quantity: 0,
                    });
                }
                catch (e)
                {
                    console.error(e);
                }
            }

            // for (let i = 0; i < accountNft[ contractAddress ].amount; i += 1)
            // {
            // try
            // {
            //     const dataNFT = await this.getMetadataAsync(accountNft[ contractAddress ].tokenId[ i ], contract);

            //     accountNft[ contractAddress ].nftData.push({
            //         ...dataNFT,
            //         contractAddress,
            //         tokenId: accountNft[ contractAddress ].tokenId[ i ],
            //         tokenType: accountNft[ contractAddress ].isErc1155 ? 'ERC-1155' : 'ERC-721',
            //         hash: accountNft[ contractAddress ].tokenHash[ accountNft[ contractAddress ].tokenId[ i ].toString() ],
            //         quantity: 0,
            //     });
            // }
            // catch (e)
            // {
            //     console.error(e);
            // }
            // }

            if (accountNft[ contractAddress ].isErc1155)
            {
                accountNft[ contractAddress ].nftData.map(nft =>
                {
                    nft.quantity = accountNft[ contractAddress ].tokenIdAll[ nft.tokenId ];
                })
                // accountNft[ contractAddress ].tokenIdAll.forEach((tokenID: number) =>
                // {
                //     const hasNftByTokenId = accountNft[ contractAddress ].nftData.find(nft => nft.tokenId === tokenID);

                //     if (hasNftByTokenId)
                //     {
                //         hasNftByTokenId.quantity += 1;
                //     }
                // });
            }

            resNFTToken.push(accountNft[ contractAddress ].nftData);
            accountNft[ contractAddress ].isLoadNFT = true;
        }

        return resNFTToken;
    }


    public async loadLogs(_url: string, _fromBlock: number): Promise<ILog[]>
    {
        let fromBlock = _fromBlock;
        let result = [];
        let requestAgain = false;

        do
        {
            try
            {
                const url = `${ _url }&startblock=${ fromBlock }`;
                const response = await this.myFetch(url);

                const data = response;

                if (data.message.includes("NOTOK"))
                {
                    console.debug("Query Timeout occured. Retrying...");
                    await sleep(1000);
                    continue;
                }

                if (response.status != 1)
                {
                    // console.error(`Cant download logs. Status ${ response.status }`);
                    break;
                }

                if (data.status != "1")
                {
                    console.debug(data);
                    console.error("Cant download and decode logs ");
                }

                const logs = data.result;

                if (!!requestAgain && result.length > 0)
                {
                    result = this.getUniqueLogs(result, logs, fromBlock);
                }
                else
                {
                    result = [ ...result, ...logs ];
                }

                // console.log(`Downloaded ${ logs.length } logs. Total ${ result.length }`);

                if (logs.length >= 10000)
                {
                    requestAgain = true;
                    fromBlock = Number(logs[ logs.length - 1 ].blockNumber);
                    await sleep(1000);
                    continue;
                }

                break;

            }
            catch (error)
            {
                console.error(`Cant download logs.`);
                console.error(error);
                break;
            }
        }
        while (true);

        return result;
    }

    public async myFetch(url: string)
    {
        let response;
        try
        {
            response = await fetch(url).then(async res =>
            {

                const data = <any>await res.json();
                return data;

            });

            response.message.includes("NOTOK");
        }
        catch (e)
        {
            console.debug('Used endpoint...');

            response = await this.http.post('/api/nft-endpoint/get-nft-history', { url: url }).toPromise();

            if (response.data)
            {
                response = response.data;
            }
            else
            {
                response = { message: "Error", status: -1 };
            }
        }

        return response;
    }


    public async getMetadataAsync(_tokenId: string, _contract: any): Promise<any>
    {
        const strKey = _tokenId.toString() + (await _contract.getAddress()).toString();
        const key = 'nft' + (await getSha256HashAsync(strKey));

        let storedValue;
        try
        {
            storedValue = await this.indexDBService.get(key);
            if (!storedValue)
            {
                const response = await this.loadTokenUrl(_tokenId, _contract);
                storedValue = response;
                if (!!storedValue)
                {
                    const isStoredSuccess = await this.indexDBService.set(key, storedValue);
                }
            }
        }
        catch (err)
        {
            console.error(`${ Date.now() } = getMetadata(${ key }): ${ err.message }`);
        }
        finally
        {
            return storedValue;
        }
    }

    public async loadTokenUrl(_tokenId: string, _contract: any)
    {
        let result, resJson;
        try
        {
            const isErc1155 = !_contract.tokenURI;
            const tokenID = _tokenId.toString().indexOf('.') === -1 ? BigInt(_tokenId) : FixedNumber.fromValue(BigInt(_tokenId));
            const erc1155ReplaceableTokenId = ethers.zeroPadValue(ethers.toBeHex(BigInt(_tokenId)), 32).slice(2)
            const tokenUri =
                isErc1155 ?
                    (await _contract.uri(tokenID)).replace('{id}', erc1155ReplaceableTokenId) :
                    await _contract.tokenURI(tokenID);
            const typeUrl = this.getTypeUrl(tokenUri);

            if (typeUrl == TokenUrlType.ipfs)
            {
                let data = await this.loadIpfs(tokenUri);
                this.prepareImageIpfs(data);
                resJson = { data: data }
            }
            else if (typeUrl !== TokenUrlType.base64)
            {
                resJson = await this.http.post('/api/nft-endpoint/get-metadata', { url: tokenUri }).toPromise();
            }
            else if (typeUrl == TokenUrlType.base64)
            {
                let data: string = <string>tokenUri.split('base64,')[ 1 ];
                let response = Buffer.from(data, 'base64').toString('utf-8');

                resJson = { data: JSON.parse(response) };
            }
            else
            {
                console.debug({ tokenUri });
            }

            result = { ...resJson, tokenUri: tokenUri };

        }
        catch (e)
        {
            console.debug(e);
        }

        return result;
    }

    private async loadIpfs(uri)
    {
        const SCHEME_IPFS = 'ipfs://';
        const httpIpfsRegex = new RegExp(/(.*)\/ipfs\/(.*)/);

        let subUri;

        if (uri.startsWith(SCHEME_IPFS))
        {
            subUri = uri.substring(SCHEME_IPFS.length);
            uri = this.choiceIpfs() + subUri;
        }
        else if (httpIpfsRegex.test(uri))
        {
            subUri = uri.match(httpIpfsRegex)[ 2 ];
            uri = this.choiceIpfs() + subUri;
        }

        let url = uri;
        let response;

        response = await fetch(url).then(async res =>
        {

            const data = <any>await res.json();
            return data;

        });


        return response;
    }

    private async loadERC1155Logs(chainId: number, address: string, fromBlock: number = 0): Promise<ILogNFT[]>
    {
        const actualBlock = fromBlock === 0 ? fromBlock : fromBlock - 10000;
        const blockchain = await this.networksService.getBlockchainByChainId(chainId);
        const logs: any[] = [];

        if (!blockchain)
        {
            return logs;
        }

        if (blockchain.testnet && blockchain.chain_id === 80001)
        {
            return await this.loadERC1155LogsTestnet(chainId, address, fromBlock);
        }
        else if (blockchain.testnet)
        {
            return logs;
        }

        const addressLowerCase = address.toLowerCase();
        const url = await this.prepareUrlERC1155(chainId, addressLowerCase);
        if (!url)
        {
            return [];
        }

        const logs1155 = await this.loadLogs(url, fromBlock);
        return logs1155.map(log => ({ ...log, type: NFT_SPECIFICATION.erc1155 }));
    }

    private async loadERC1155LogsTestnet(chainId: number, address: string, fromBlock: number = 0): Promise<ILogNFT[]>
    {
        let chainLogs = [];
        const provider = new ethers.JsonRpcProvider(
            environment[ chainId ].rpc[ 0 ],
            chainId
        );

        const latestBlock = Number(await provider.getBlockNumber());
        // const startBlock = fromBlock === 0 ? latestBlock - 100000 : fromBlock;
        const startBlock = latestBlock - 100000;

        const erc1155Interface = new ethers.Interface(ERC1155_METADATA_ABI);
        const topicTo = erc1155Interface.encodeFilterTopics('TransferSingle', [ null, null, address, null, null ]);
        const topicFrom = erc1155Interface.encodeFilterTopics('TransferSingle', [ null, address, null, null, null ]);

        const logsConfig = {
            fromBlock: Number(startBlock),
            toBlock: Number(latestBlock)
        };

        let logsFrom = [], logsTo = [], countLoad = 3, isReload = false;

        do
        {
            countLoad -= 1;
            isReload = false;
            try
            {
                logsFrom = await provider.getLogs({
                    topics: topicFrom,
                    ...logsConfig
                });

                logsTo = await provider.getLogs({
                    topics: topicTo,
                    ...logsConfig
                });
            } catch (e)
            {
                isReload = true;
            }

        } while (isReload && countLoad > 0)

        if (logsTo?.length > 0 && logsFrom?.length > 0 && logsTo.at(-1).blockNumber > logsFrom.at(-1).blockNumber)
        {
            let lastLog;

            lastLog = logsTo.pop();
            chainLogs.push(...logsTo, ...logsFrom, lastLog);
        }
        else
        {
            chainLogs.push(...logsTo, ...logsFrom);
        }

        let resLogs = [];

        for (let log of chainLogs)
        {
            try
            {
                const parsed = erc1155Interface.parseLog(log);
                let metaData = {
                    name: 'ERC1155',
                    symbol: 'ERC1155'
                };

                resLogs.push({
                    ...parsed,
                    ...parsed.args,
                    hash: log.transactionHash,
                    tokenID: parsed.args.id.toHexString(),
                    blockNumber: log.blockNumber,
                    contractAddress: log.address.toLowerCase(),
                    tokenName: metaData.name,
                    tokenSymbol: metaData.symbol,
                    type: NFT_SPECIFICATION.erc1155,
                    tokenValue: parsed.args.value.toNumber(),
                })

            }
            catch (e)
            {
                console.error('Logs error');
                // console.error(e);
            }
        }

        console.log(resLogs);
        return resLogs;
    }

}
